import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { EditorModule } from 'primeng/editor';
import { FileUploadModule } from 'primeng/fileupload';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { Eignungstest } from '../../../models/eignungstest.model';
import { EignungstestService } from '../../../services/eignungstest.service';

import { QuillModule } from 'ngx-quill';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { debounceTime } from 'rxjs/operators';
import { EignungstestInvitation } from '../../../models/eignungstestInvitation';
import { Residence } from '../../../models/residence.model';
import { user } from '../../../models/user.model';
import { ActivityService } from '../../../services/activity.service';
import { AuthService } from '../../../services/auth.service';
import { NotificationService } from '../../../services/notification.service';
import { UserService } from '../../../services/user.service';

import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { DialogResult } from 'src/app/workflow/models/dialog-result.model';

let Quill: any = null;

@Component({
  selector: 'app-create-eignungstest-dialog',
  standalone: true,
  templateUrl: './create-eignungstest-dialog.component.html',
  styleUrls: ['./create-eignungstest-dialog.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    DropdownModule,
    EditorModule,
    ButtonModule,
    FloatLabelModule,
    CalendarModule,
    MultiSelectModule,
    InputNumberModule,
    FileUploadModule,
    CheckboxModule,
    InputSwitchModule,
    InputTextModule,
    QuillModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class CreateEignungstestDialogComponent implements OnInit {
  testForm: FormGroup;
  users: user[] = [];
  pdfGenerated: string = '';
  uploadedPdfFile: File | null = null;

  emailBody: string = '';

  selectedUser: user | null = null;
  residences?: Residence[];
  isBrowser: boolean;

  quillInstance: any;
  quillModules: any;
  quillFormats: string[];

  manualEmailEdit: boolean = false;

  isDialogDataReady: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private fb: FormBuilder,
    private eignungstestService: EignungstestService,
    private userService: UserService,
    private activityService: ActivityService,
    private authService: AuthService,
    private ref: DynamicDialogRef,
    private notificationService: NotificationService,
    public config: DynamicDialogConfig,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.selectedUser = this.config.data?.selectedUser;

    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const localDate = `${day}.${month}.${year}`;

    this.testForm = this.fb.group({
      userId: [
        this.selectedUser ? this.selectedUser.userId : '',
        this.selectedUser ? [] : [Validators.required],
      ],
      testAction: ['result', Validators.required],
      testDate: [localDate, Validators.required],
      scoreIT: [''],
      scoreAllg: [''],
      scoreEng: [''],
      scoreWirt: [''],
      emailText: ['', Validators.required],
      pdfUrl: [''],
    });

    /*if (this.selectedUser) {
      this.testForm.get('userId')?.disable();
    }*/
  }

  ngOnInit(): void {
    if (this.selectedUser && this.config?.data?.selectedUser) {
      this.testForm.get('userId')?.disable();
      this.testForm.patchValue({ userId: this.selectedUser.userId });
    }

    this.userService.getUserWithQuery(5).subscribe((users) => {
      this.users = users.filter((u) => u.isLead === true);

      if (
        this.selectedUser &&
        !this.users.some((u) => u.userId === this.selectedUser!.userId)
      ) {
        this.users.push(this.selectedUser);
      }

      if (this.selectedUser) {
        this.testForm.get('userId')?.disable();
        this.testForm.patchValue({ userId: this.selectedUser.userId });
        this.onActionChanged(this.testForm.get('testAction')!.value);
      }
    });

    if (this.isBrowser) {
      import('quill')
        .then(({ default: QuillImport }) => {
          Quill = QuillImport;

          this.quillModules = {
            toolbar: [
              ['bold', 'italic', 'underline', 'strike'],
              [{ header: 1 }, { header: 2 }],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ script: 'sub' }, { script: 'super' }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ direction: 'rtl' }],
              [{ size: [] }], // Normal, Small, Large, Huge
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              [{ color: [] }, { background: [] }],
              [{ font: [] }], // Sans Serif, Serif, Monospace...
              [{ align: [] }],
              ['link', 'image' /* 'video', 'code-block' */],
              ['clean'], // formatting
            ],
          };

          this.quillFormats = [
            'bold',
            'italic',
            'underline',
            'strike',
            'header',
            'list',
            'bullet',
            'script',
            'indent',
            'direction',
            'size',
            'color',
            'background',
            'font',
            'align',
            'link',
            'image',
            'video',
            'code-block',
            'clean',
          ];
        })
        .catch((error) => {
          console.error('Quill yüklenemedi:', error);
        });
    }

    this.testForm
      .get('emailText')
      ?.valueChanges.pipe(debounceTime(300))
      .subscribe((htmlContent: string) => {
        this.extractScoresFromQuill(htmlContent);
      });

    ['scoreIT', 'scoreAllg', 'scoreEng', 'scoreWirt'].forEach((field) => {
      this.testForm
        .get(field)
        ?.valueChanges.pipe(debounceTime(300))
        .subscribe(() => {
          this.updateEmailText();
        });
    });

    ['testDate'].forEach((field) => {
      this.testForm.get(field)?.valueChanges.subscribe(() => {
        this.updateEmailText();
      });
    });
  }

  private extractScoresFromQuill(htmlContent: string): void {
    if (!htmlContent) return;

    let scoreIT = 0;
    let scoreAllg = 0;
    let scoreEng = 0;
    let scoreWirt = 0;

    // 1) IT-Fachfragen
    const itRegex = /(\d+(?:\.\d+)?)\s*%?\s*IT-Fachfragen/i;
    const matchIT = itRegex.exec(htmlContent);
    if (matchIT) {
      scoreIT = parseFloat(matchIT[1]) || 0;
    }

    // 2) Allgemeinwissen
    const allgRegex = /(\d+(?:\.\d+)?)\s*%?\s*Allgemeinwissen/i;
    const matchAllg = allgRegex.exec(htmlContent);
    if (matchAllg) {
      scoreAllg = parseFloat(matchAllg[1]) || 0;
    }

    // 3) Englisch
    const engRegex = /(\d+(?:\.\d+)?)\s*%?\s*Englisch/i;
    const matchEng = engRegex.exec(htmlContent);
    if (matchEng) {
      scoreEng = parseFloat(matchEng[1]) || 0;
    }

    // 4) Wirtschaft
    const wirtRegex = /(\d+(?:\.\d+)?)\s*%?\s*Wirtschaft/i;
    const matchWirt = wirtRegex.exec(htmlContent);
    if (matchWirt) {
      scoreWirt = parseFloat(matchWirt[1]) || 0;
    }

    this.testForm.patchValue(
      {
        scoreIT,
        scoreAllg,
        scoreEng,
        scoreWirt,
      },
      { emitEvent: false },
    );
  }

  private convertQuillClassesToInline(html: string): string {
    let result = html;
    result = result.replace(
      /class="([^"]*ql-align-center[^"]*)"/gi,
      'style="display:block; text-align:center;"',
    );

    result = result.replace(
      /class="([^"]*ql-align-center[^"]*)"/gi,
      'style="display:block; text-align:center; width:100%;"',
    );

    result = result.replace(
      /<p class="([^"]*ql-align-center[^"]*)">(.*?)<\/p>/gi,
      '<center><p>$2</p></center>',
    );

    result = result.replace(
      /class="([^"]*ql-align-center[^"]*)"/gi,
      'style="display:flex; justify-content:center;"',
    );

    return result;
  }

  onEditorCreated(quill: any) {
    this.quillInstance = quill;
    quill.on('text-change', () => {
      this.manualEmailEdit = true;
      const htmlContent = quill.root.innerHTML;
      this.extractScoresFromQuill(htmlContent);
    });
  }

  insertDangerousHTML(): void {
    if (!this.quillInstance) return;

    const rawHtml = `<h1 style="color:red; text-align:center;">Tehlikeli HTML</h1>`;
    this.quillInstance.clipboard.dangerouslyPasteHTML(rawHtml);
  }

  closeDialog(result?: DialogResult): void {
    this.ref.close(result);

    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const localDate = `${day}.${month}.${year}`;

    this.testForm.reset({
      userId: '',
      testAction: 'result',
      testDate: localDate,
      scoreIT: '',
      scoreAllg: '',
      scoreEng: '',
      scoreWirt: '',
      emailText: '',
      pdfUrl: '',
    });

    this.emailBody = '';
    this.selectedUser = null;
  }

  submitTest(): void {
    if (!this.testForm.valid) return;
    const formValue = this.testForm.getRawValue();

    if (formValue.testAction === 'invitation') {
      const invitationData: EignungstestInvitation = {
        invitationId: 0,
        userId: formValue.userId,
        emailText: formValue.emailText,
        creatorId: this.authService.getCurrentUserId(),
      };

      this.eignungstestService
        .sendCreateAndSendInvitation(invitationData)
        .subscribe({
          next: (createdInvitation) => {
            this.activityService.fetchUserActivities().subscribe();
            this.notificationService.success(
              'Einladung wurde erfolgreich gesendet!',
            );
            this.closeDialog({
              success: true,
              activityId: createdInvitation.activityId,
            });
          },
          error: (err) => {
            console.error('Fehler bei Einladung:', err);
          },
        });
    } else {
      let finalHtml = formValue.emailText;
      finalHtml = this.convertQuillClassesToInline(finalHtml);
      const [dayStr, monthStr, yearStr] = formValue.testDate.split('.');
      const isoDate = `${yearStr}-${monthStr}-${dayStr}`;

      const newTest: Eignungstest = {
        userId: formValue.userId,
        testDate: isoDate,
        emailText: finalHtml,
        creatorId: this.authService.getCurrentUserId(),
        pdfUrl: '',
      };

      this.eignungstestService.createTest(newTest).subscribe({
        next: (createdTest) => {
          this.eignungstestService
            .generateTestResultPdf(createdTest.testId!)
            .subscribe({
              next: (pdfUrl) => {
                createdTest.pdfUrl = pdfUrl;
                this.eignungstestService.updateTest(createdTest).subscribe({
                  next: () => {
                    this.closeDialog({
                      success: true,
                      activityId: createdTest.activityId,
                    });

                    const testWithActivityId = createdTest as any;

                    if (testWithActivityId.activityId) {
                      this.activityService
                        .getActivityById(testWithActivityId.activityId)
                        .subscribe({
                          next: (fullActivity) => {
                            this.activityService.addActivity(fullActivity);
                            this.notificationService.success(
                              'PDF wurde erstellt und E-Mail gesendet!',
                            );
                            this.ref.close(fullActivity);
                            this.closeDialog();
                          },
                          error: () => {
                            this.notificationService.warn(
                              'E-Mail wurde gesendet, aber die Aktivität konnte nicht geladen werden. Bitte aktualisieren Sie die Seite.',
                            );
                            this.closeDialog();
                          },
                        });
                    } else {
                      this.notificationService.success(
                        'PDF wurde erstellt und E-Mail gesendet!',
                      );
                      this.closeDialog();
                    }

                    this.eignungstestService
                      .sendTestResultEmail(createdTest.testId!)
                      .subscribe({
                        next: () => {
                          // this.notificationService.success(
                          //  'PDF wurde erstellt und E-Mail gesendet!'
                          // );
                          this.closeDialog();
                        },
                        error: (err) =>
                          console.error('Fehler beim Senden der E-Mail:', err),
                      });
                  },
                  error: (err) => console.error('Update fehlgeschlagen:', err),
                });
              },
              error: (err) => console.error('Fehler beim PDF-Generieren:', err),
            });
        },
        error: () => {
          this.notificationService.error(
            'Fehler',
            'Test konnte nicht erstellt werden!',
          );
        },
      });
    }
  }

  sendEmailInvitation(testId: number): void {
    this.eignungstestService.sendTestInvitationEmail(testId).subscribe({
      next: () => console.log('Einladung E-Mail erfolgreich gesendet!'),
      error: (err) => console.error('Fehler beim Senden der Einladung:', err),
    });
  }

  updateTestAndSendEmail(test: Eignungstest): void {
    this.eignungstestService.updateTest(test).subscribe({
      next: () => {
        this.sendEmail(test.testId!);
      },
      error: (err) => console.error('Fehler beim Update des Tests:', err),
    });
  }

  sendEmail(testId: number): void {
    this.eignungstestService.sendTestResultEmail(testId).subscribe({
      next: () => console.log('E-Mail erfolgreich gesendet!'),
      error: (err) => console.error('Fehler beim Senden der E-Mail:', err),
    });
  }

  loadUsers(): void {
    this.userService.getUserWithQuery(1).subscribe({
      next: (users) => {
        this.users = users.filter((u) => u.isLead === true);

        if (
          this.selectedUser &&
          !this.users.some((u) => u.userId === this.selectedUser!.userId)
        ) {
          this.users.push(this.selectedUser);
        }

        const action = this.testForm.get('testAction')?.value;
        this.onActionChanged(action);
      },
      error: (err) => {
        console.error('Fehler beim Laden der Benutzer:', err);
      },
    });

    ['scoreIT', 'scoreAllg', 'scoreEng', 'scoreWirt'].forEach((field) => {
      this.testForm
        .get(field)
        ?.valueChanges.pipe(debounceTime(300))
        .subscribe(() => {
          this.updateEmailText();
        });
    });

    ['testDate', 'selectedTime'].forEach((field) => {
      this.testForm.get(field)?.valueChanges.subscribe(() => {
        this.updateEmailText();
      });
    });

    this.onActionChanged(this.testForm.get('testAction')?.value);
  }

  generatePdf(test: Eignungstest): void {
    this.pdfGenerated = `Eignungstest_${test.userId}_${test.testDate}.pdf`;
  }

  onFileUpload(event: any): void {
    if (event.files.length > 0) {
      this.uploadedPdfFile = event.files[0];
    }
  }

  handlePdfSelection(event: any): void {
    if (event.value === 'auto') {
      this.testForm.patchValue({ pdfUrl: 'generated' });
    } else {
      this.testForm.patchValue({ pdfUrl: '' });
    }
  }

  calculateTotalScoreText(formValue: any): string {
    const sIT = parseFloat(formValue.scoreIT) || 0;
    const sAllg = parseFloat(formValue.scoreAllg) || 0;
    const sEng = parseFloat(formValue.scoreEng) || 0;
    const sWirt = parseFloat(formValue.scoreWirt) || 0;

    const total = sIT + sAllg + sEng + sWirt;

    return total.toFixed(2) + '%';
  }

  onActionChanged(action: string): void {
    if (action === 'invitation') {
      const userId = this.testForm.get('userId')?.value;
      const foundUser = this.users.find((u) => u.userId === userId);
      if (!foundUser) {
        this.testForm.patchValue({
          emailText: `Guten Tag,<br><br>
          wir laden Sie herzlich zum Eignungstest ein. ...
          `,
        });
        return;
      }

      let genderTitle: string;
      if (foundUser.gender === 'male') {
        genderTitle = 'Sehr geehrter Herr';
      } else if (foundUser.gender === 'female') {
        genderTitle = 'Sehr geehrte Frau';
      } else {
        genderTitle = 'Sehr geehrte Damen und Herren';
      }

      const namePart =
        genderTitle === 'Sehr geehrte Damen und Herren'
          ? ''
          : ` ${foundUser.lastname}`;

      const invitationText = `
        ${genderTitle}  ${namePart},<br><br>
        Vielen Dank für Ihr Interesse an einer Umschulung bei der WAVE Campus GmbH. <br><br>
        Um Ihre Vorkenntnisse und Fähigkeiten besser einschätzen zu können möchten wir Sie bitten einen Eignungstest durchzuführen. <br><br>
        wir laden Sie herzlich zum Eignungstest unter dem nachfolgenden Link ein. Bitte klicken Sie auf den folgenden Link, um den Test zu starten: 
        <a href="https://eignungstest.wave-campus.de/">Eignungstest starten</a><br><br>
        Bitte führen Sie den Test innerhalb der nächsten 5 Tage durch. <br><br>

        Vielen Dank für Ihre Bemühungen.<br><br>

        Mit freundlichen Grüßen
      `;

      this.testForm.patchValue({ emailText: invitationText });
    } else if (action === 'result') {
      const userId = this.testForm.get('userId')?.value;
      if (userId) {
        this.selectedUser = this.users.find((u) => u.userId === userId) || null;
        this.updateEmailText();
      }
    }
  }

  onUserChanged(): void {
    const userId = this.testForm.get('userId')?.value;
    this.selectedUser = this.users.find((u) => u.userId === userId) || null;

    this.testForm.patchValue({
      scoreIT: 0,
      scoreAllg: 0,
      scoreEng: 0,
      scoreWirt: 0,
    });

    if (
      this.testForm.get('testAction')?.value === 'result' &&
      this.selectedUser
    ) {
      this.updateEmailText();
    }
  }

  getEmailHeader(testDate: string): string {
    const careerInterest = this.selectedUser?.extraUserInfo?.careerInterest
      ? this.selectedUser.extraUserInfo.careerInterest
      : '<em>(keine Auswahl getroffen)</em>';

    return `
    <p style="font-weight: bold;">Bescheinigung über das Ergebnis Eignungstest</p>
  

    <p style="font-weight: bold;">hier: Umschulung zum ${careerInterest}</p><br /><br />`;
  }

  updateEmailText(): void {
    if (this.testForm.get('testAction')?.value !== 'result') return;

    const formValue = this.testForm.getRawValue();

    const selectedUser = this.users.find((u) => u.userId === formValue.userId);
    if (!selectedUser) return;

    const header = this.getEmailHeader(formValue.testDate);
    const body = this.fillEmailTemplate(formValue, selectedUser);
    const fullText = header + body;

    this.testForm.patchValue({ emailText: fullText }, { emitEvent: false });
  }

  fillEmailTemplate(formValue: any, user: user): string {
    //const genderTitle = user.gender === 'male' ? 'Herr' : 'Frau';
    const testDate = formValue.testDate;
    const userId = this.testForm.get('userId')?.value;
    const foundUser = this.users.find((u) => u.userId === userId);
    if (!foundUser) {
      this.testForm.patchValue({
        emailText: `Guten Tag,<br><br>
        <p>
  Sie haben am <b> .....   </b> den Eignungstest durchgeführt.
</p>
        `,
      });
    }
    let genderTitle: string;
    if (foundUser.gender === 'male') {
      genderTitle = 'Sehr geehrter Herr';
    } else if (foundUser.gender === 'female') {
      genderTitle = 'Sehr geehrte Frau';
    } else {
      genderTitle = 'Sehr geehrte Damen und Herren';
    }

    const sIT = formValue.scoreIT || 0;
    const sAllg = formValue.scoreAllg || 0;
    const sEng = formValue.scoreEng || 0;
    const sWirt = formValue.scoreWirt || 0;

    const total = sIT + sAllg + sEng + sWirt;
    const scoreText = total + '%';

    const namePart =
      genderTitle === 'Sehr geehrte Damen und Herren'
        ? ''
        : ` ${foundUser.lastname}`;

    return `

<p>
 ${genderTitle}  ${namePart},
</p><br>

<p>
vielen Dank an Ihre Teilnahme am Eignungstest. Sie haben den heutigen Eignungstest mit <br> einem Gesamtergebnis von ${scoreText}  bestanden.
</p>

<p>
Wir freuen uns Ihnen somit mitteilen zu dürfen, dass Sie die Voraussetzungen für <br> die Teilnahme an der Umschulung erfüllen. Gerne reservieren wir Ihnen einen Platz im nächsten Kurs.
</p>

<p>
Bei weiteren Fragen zögern Sie bitte nicht uns jederzeit zu kontaktieren.
</p>


<p>Mit freundlichen Grüßen</p>
    `;
  }

  /*ngAfterViewInit(): void {
    setTimeout(() => {
      this.selectedUser = this.config.data?.selectedUser || null;
      this.isDialogDataReady = true;
    }, 0);
  }*/
}
